import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  type CollisionDetection,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ChevronDownIcon, HorizontaLDots } from "../icons";
import PrivacyUnlockButton from "../components/header/PrivacyUnlockButton";

const DealerTemplateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 5h16v11H8l-4 4V5z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <line x1="7.5" y1="9.5" x2="13.5" y2="9.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="7.5" y1="12.5" x2="16" y2="12.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <rect x="14.5" y="8" width="3" height="3" rx="0.5" stroke="currentColor" strokeWidth="1.2" />
  </svg>
);

const CustomerIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="8" r="3" stroke="currentColor" strokeWidth="1.6" />
    <path d="M3.5 20c0-3 2.5-5.5 5.5-5.5s5.5 2.5 5.5 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    <rect x="15" y="4" width="6" height="10" rx="1" stroke="currentColor" strokeWidth="1.4" />
    <line x1="16.5" y1="7" x2="19.5" y2="7" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="16.5" y1="9.5" x2="19.5" y2="9.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    <line x1="16.5" y1="12" x2="18.5" y2="12" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const FormTemplateIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 3h9l4 4v12a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M15 3v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <line x1="8" y1="11" x2="16" y2="11" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="8" y1="14" x2="16" y2="14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="8" y1="17" x2="13" y2="17" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);


const ArchiveIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="4.5" width="17" height="4" rx="1" stroke="currentColor" strokeWidth="1.5" />
    <path d="M5 8.5V18a1.5 1.5 0 001.5 1.5h11A1.5 1.5 0 0019 18V8.5"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M10 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// 업무 자료함(스크린챗 #87) — 매장이 함께 쓰는 파일·링크 보관함
const LibraryIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 7a1.5 1.5 0 011.5-1.5h4l1.8 2H19a1.5 1.5 0 011.5 1.5v9A1.5 1.5 0 0119 19.5H5A1.5 1.5 0 013.5 18V7z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 13h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

import { useSidebar } from "../context/SidebarContext";
import { useAuth } from "../context/AuthContext";
import { useUiZoom } from "../context/UiZoomContext";
import { useTheme, type ThemePreference } from "../context/ThemeContext";
import { APP_NAME } from "../constants";
import { canAccessCounselTraining, type PageGroupKey } from "../../shared/rbac";
import { CHILD_STORE_COUNSEL_MENU_PATH } from "../../shared/child-store-menu";
import { SCAN_AREA_KEY } from "../../shared/account-areas";
import { LEAD_DEMO_NAV } from "../lib/leadDemo";
import { useEdition } from "../context/EditionContext";
import {
  enabledExternalFeatures,
  EXTERNAL_NAV_GROUP_LABEL,
} from "../../shared/external-edition";
import {
  fetchSidebarOrder,
  updateSidebarOrder,
  type SidebarFolder,
  type SidebarOrderPreference,
} from "../api/tenants";
import { useCounselUnansweredCount } from "../hooks/counsel";

// LNB 그룹 라벨 → RBAC 페이지그룹 키 매핑. 여기 있는 그룹만 권한(can)으로 통째 숨긴다.
// "관리자" 그룹은 매핑하지 않는다 — 기존 isAdmin() 로직 그대로 유지.
const GROUP_LABEL_TO_KEY: Record<string, PageGroupKey> = {
  "개통 CRM": "crm",
  "정책관리": "policy",
  "재고관리": "inventory",
  "카톡 상담": "kakao_counsel",
};

const TenantAdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M4 9l8-5 8 5v10a1 1 0 01-1 1H5a1 1 0 01-1-1V9z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M9 20v-6h6v6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const UsersAdminIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="9" cy="9" r="3" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="17" cy="10" r="2.5" stroke="currentColor" strokeWidth="1.3" />
    <path d="M3 19c0-3 2.5-5 6-5s6 2 6 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M15.5 19c0-2 1.5-3.5 4-3.5S22 17 22 19" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

// 활동 로그 — 시간축 위 행동 파형(막대) 아이콘
const ActivityLogIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <line x1="3" y1="20" x2="21" y2="20" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <rect x="5" y="12" width="2.6" height="6" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="10.7" y="8" width="2.6" height="10" rx="1" stroke="currentColor" strokeWidth="1.3" />
    <rect x="16.4" y="14" width="2.6" height="4" rx="1" stroke="currentColor" strokeWidth="1.3" />
  </svg>
);

const InventoryDeviceIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="7" y="3" width="10" height="18" rx="2"
      stroke="currentColor" strokeWidth="1.5" />
    <line x1="11" y1="18" x2="13" y2="18"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <line x1="9.5" y1="6.5" x2="14.5" y2="6.5"
      stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
  </svg>
);

const InventoryHoldingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 7l9-4 9 4-9 4-9-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M3 12l9 4 9-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M3 17l9 4 9-4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

const PolicyPipelineIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="5.5" cy="6" r="2" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="12" cy="12" r="2" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="18.5" cy="18" r="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M7 7.5l3.5 3M13.5 13.5l3.5 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// 업로드 내역 대장 — 가로줄(목록) + 시계(이력) 느낌
const PolicyCalendarIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M3.5 9h17M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <path d="M6.5 13h5M9 16.5h7" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// 데이터 카탈로그 — 데이터베이스 실린더 모양
const PolicyCatalogIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="12" cy="6" rx="7.5" ry="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M4.5 6v12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3V6" stroke="currentColor" strokeWidth="1.5" />
    <path d="M4.5 12c0 1.66 3.36 3 7.5 3s7.5-1.34 7.5-3" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

const PolicyBoardIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <line x1="3.5" y1="9" x2="20.5" y2="9" stroke="currentColor" strokeWidth="1.4" />
    <line x1="9" y1="9" x2="9" y2="19.5" stroke="currentColor" strokeWidth="1.4" />
    <line x1="14.5" y1="9" x2="14.5" y2="19.5" stroke="currentColor" strokeWidth="1.4" />
  </svg>
);

// 시세표 작성하기 — 가격이 적힌 안내 문서. 문서 위 가격 그래프 + 태그 모양
const PolicyQuoteSheetIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 3h8l4 4v12a2 2 0 01-2 2H6a2 2 0 01-2-2V5a2 2 0 012-2z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M14 3v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    <path d="M8 15l2.5-2.5 2 2 3.5-3.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    <circle cx="16" cy="11" r="0.9" fill="currentColor" />
  </svg>
);

// 업체별 모델 그룹 관리 — 업체마다 취급 모델을 그룹 칩으로 묶어 관리. 겹친 사각형(그룹) 모양
const PolicyModelGroupsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3.5" y="3.5" width="9" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <rect x="11.5" y="11.5" width="9" height="9" rx="2" stroke="currentColor" strokeWidth="1.5" />
    <path d="M8 16.5H6a2.5 2.5 0 01-2.5-2.5v-2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
    <path d="M16 7.5h2A2.5 2.5 0 0120.5 10v2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// 정책 설정 — 개발자·관리자가 들여다보는 기준 데이터 묶음(업체·별칭·요금제 구간). 톱니바퀴 모양
const PolicySettingsIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5" />
    <path d="M12 2.5v2.5M12 19v2.5M21.5 12H19M5 12H2.5M18.7 5.3l-1.8 1.8M7.1 16.9l-1.8 1.8M18.7 18.7l-1.8-1.8M7.1 7.1L5.3 5.3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

// 카톡 상담 — 말풍선 모양
const KakaoCounselIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M12 4C7 4 3 7.1 3 11c0 2.4 1.5 4.5 3.9 5.8L6 20l3.7-1.9c.7.1 1.5.2 2.3.2 5 0 9-3.1 9-7s-4-7.3-9-7.3z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <circle cx="9" cy="11" r="0.9" fill="currentColor" />
    <circle cx="12" cy="11" r="0.9" fill="currentColor" />
    <circle cx="15" cy="11" r="0.9" fill="currentColor" />
  </svg>
);

// 통화 녹음 정리 — 마이크 모양
const CallRecordingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="9" y="3" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M6 11a6 6 0 0 0 12 0M12 17v3M9 20h6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 문자 발송 예약 — 봉투 위에 시계(예약)를 얹은 모양
const ScheduledMessageIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 6.5A1.5 1.5 0 0 1 5.5 5h13A1.5 1.5 0 0 1 20 6.5v6A1.5 1.5 0 0 1 18.5 14h-6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M4.5 6.5l7.5 5 7.5-5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <circle cx="7.5" cy="17" r="3.3" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M7.5 15.4V17l1.1 1.1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// 페이백(고객에게 돌려주는 계좌 입금) — 지갑 모양
const PaybackIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M4 8.5A1.5 1.5 0 0 1 5.5 7h13A1.5 1.5 0 0 1 20 8.5v9A1.5 1.5 0 0 1 18.5 19h-13A1.5 1.5 0 0 1 4 17.5v-9Z"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
    />
    <path
      d="M4 9L15.5 5"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
    <circle cx="16" cy="13" r="1.3" fill="currentColor" />
  </svg>
);

// 정산관리 — 아직 못 받은 정산을 챙기는 화면. 계산기 모양(금액을 맞춰본다).
const SettlementIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect
      x="4.75"
      y="3.75"
      width="14.5"
      height="16.5"
      rx="2"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <rect
      x="7.75"
      y="6.75"
      width="8.5"
      height="3.5"
      rx="1"
      stroke="currentColor"
      strokeWidth="1.5"
    />
    <circle cx="8.75" cy="13.75" r="1" fill="currentColor" />
    <circle cx="12" cy="13.75" r="1" fill="currentColor" />
    <circle cx="15.25" cy="13.75" r="1" fill="currentColor" />
    <circle cx="8.75" cy="17" r="1" fill="currentColor" />
    <circle cx="12" cy="17" r="1" fill="currentColor" />
    <circle cx="15.25" cy="17" r="1" fill="currentColor" />
  </svg>
);

// 매장이 직접 만든 하위묶음 — 코드에 없는, 순서 편집으로만 생기는 메뉴다
const FolderIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3.5 6.5A1.5 1.5 0 015 5h4l1.8 2H19a1.5 1.5 0 011.5 1.5v9A1.5 1.5 0 0119 19H5a1.5 1.5 0 01-1.5-1.5v-11z"
      stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
  </svg>
);

type NavSubItem = {
  name: string;
  path: string;
  pro?: boolean;
  new?: boolean;
  /** 숫자가 1 이상일 때만 보이는 업무 대기 건수. */
  badge?: number;
  /** 매장이 숨긴 메뉴 — 편집 중에만 흐리게 남고 평소엔 렌더하지 않는다. */
  hidden?: boolean;
};

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  /** 링크였던 메뉴를 묶음으로 바꿀 때 기존 순서·숨김 저장값을 이어받는 안정 키. */
  orderKey?: string;
  new?: boolean;
  /** 숫자가 1 이상일 때만 보이는 업무 대기 건수. */
  badge?: number;
  hidden?: boolean;
  /** 매장이 직접 만든 하위묶음이면 그 키(folder:<id>). 코드에 원래 있던 메뉴는 undefined. */
  folderKey?: string;
  subItems?: NavSubItem[];
};

type NavGroup = {
  label: string;
  items: NavItem[];
  hidden?: boolean;
};

const ORDER_KEY_SEPARATOR = "\u001f";

const FOLDER_KEY_PREFIX = "folder:";

const itemOrderKey = (item: NavItem) =>
  item.orderKey ?? item.folderKey ?? item.path ?? `menu:${item.name}`;
const subItemOrderKey = (groupLabel: string, item: NavItem) =>
  `${groupLabel}${ORDER_KEY_SEPARATOR}${itemOrderKey(item)}`;
/**
 * 숨김 목록에 쓰는 키. 메뉴는 자기 orderKey(대개 경로) 하나로 식별한다 — 그래야 하위묶음에
 * 넣었다 뺐다 해도 "숨김"이 그 메뉴를 따라다닌다. 그룹만 경로가 없어 접두어를 붙인다.
 */
const hiddenGroupKey = (groupLabel: string) => `group${ORDER_KEY_SEPARATOR}${groupLabel}`;

function sortByHint<T>(values: T[], hint: string[] | undefined, key: (value: T) => string): T[] {
  if (!hint?.length) return values;
  const rank = new Map(hint.map((entry, index) => [entry, index]));
  return values
    .map((value, index) => ({ value, index, rank: rank.get(key(value)) }))
    .sort((a, b) => {
      if (a.rank == null && b.rank == null) return a.index - b.index;
      if (a.rank == null) return 1;
      if (b.rank == null) return -1;
      return a.rank - b.rank;
    })
    .map(({ value }) => value);
}

/**
 * 권한 필터가 끝난 buildNavGroups 결과에 매장이 정한 배치를 덧씌운다.
 *
 * 저장값은 메뉴를 복제하지 않고 "힌트"만 담는다 — 순서(키 배열)·숨김(키 목록)·하위묶음(정의).
 * 그래서 코드에 메뉴가 새로 생기거나 사라져도 저장값이 낡지 않는다.
 */
function applySidebarOrder(
  groups: NavGroup[],
  order: SidebarOrderPreference | null,
): NavGroup[] {
  if (!order) return groups;
  const hidden = new Set(order.hidden ?? []);
  const folders = order.folders ?? [];
  const orderedGroups = sortByHint(groups, order.groups, (group) => group.label);
  return orderedGroups.map((group) => {
    const itemOrder = order.items[group.label];
    const knownItems = new Set(itemOrder ?? []);

    // ① 매장이 만든 하위묶음을 그 그룹의 메뉴 목록에 빈 껍데기로 합류시킨다.
    const groupFolders = folders.filter((folder) => folder.group === group.label);
    const withFolders: NavItem[] = [
      ...group.items,
      ...groupFolders.map((folder) => ({
        name: folder.label,
        icon: <FolderIcon />,
        folderKey: folder.key,
        subItems: [] as NavSubItem[],
      })),
    ];
    const sorted = sortByHint(withFolders, itemOrder, itemOrderKey);

    // ② 묶음에 담기로 한 메뉴는 최상위에서 빼내 그 묶음 안으로 옮긴다.
    //    하위메뉴를 이미 가진 메뉴와 묶음 자신은 담지 않는다(3단 중첩 방지).
    const folderOfItem = new Map<string, string>();
    for (const folder of groupFolders) {
      for (const key of order.subItems[`${group.label}${ORDER_KEY_SEPARATOR}${folder.key}`] ?? []) {
        folderOfItem.set(key, folder.key);
      }
    }
    const pocket = new Map<string, NavSubItem[]>();
    const topItems: NavItem[] = [];
    for (const item of sorted) {
      const key = itemOrderKey(item);
      const targetFolder = folderOfItem.get(key);
      if (targetFolder && !item.folderKey && !item.subItems && item.path) {
        const list = pocket.get(targetFolder) ?? [];
        list.push({ name: item.name, path: item.path, new: item.new, badge: item.badge });
        pocket.set(targetFolder, list);
        continue;
      }
      topItems.push(item);
    }

    // ③ 하위 목록 순서·숨김 표시를 얹는다.
    const items = topItems.map((item) => {
      const key = itemOrderKey(item);
      const subOrder = order.subItems[subItemOrderKey(group.label, item)];
      const knownSubItems = new Set(subOrder ?? []);
      const baseSubItems = item.folderKey ? (pocket.get(item.folderKey) ?? []) : item.subItems;
      return {
        ...item,
        // 저장 이후 코드에 새로 생긴 메뉴는 기존 배치를 밀지 않고 그룹 끝에 NEW로 붙는다.
        // (묶음은 매장이 방금 만든 것이라 NEW 판정 대상이 아니다.)
        new: item.folderKey ? undefined : knownItems.has(key) ? item.new : true,
        hidden: hidden.has(key),
        subItems: baseSubItems
          ? sortByHint(baseSubItems, subOrder, (subItem) => subItem.path).map((subItem) => ({
              ...subItem,
              // 링크였던 메뉴가 묶음으로 승격된 경우 첫 하위 링크는 부모 orderKey와 같다.
              // 기존 저장값은 부모의 순서·숨김으로 승계하고, 같은 키를 자식에 또 적용하지 않는다.
              new: item.folderKey
                ? subItem.new
                : knownSubItems.has(subItem.path) ||
                    (knownItems.has(key) && subItem.path === item.orderKey)
                  ? subItem.new
                  : true,
              hidden: subItem.path === item.orderKey ? false : hidden.has(subItem.path),
            }))
          : undefined,
      };
    });
    return { ...group, items, hidden: hidden.has(hiddenGroupKey(group.label)) };
  });
}

function serializeSidebarOrder(groups: NavGroup[]): SidebarOrderPreference {
  const items: Record<string, string[]> = {};
  const subItems: Record<string, string[]> = {};
  const hidden: string[] = [];
  const folders: SidebarFolder[] = [];
  for (const group of groups) {
    if (group.hidden) hidden.push(hiddenGroupKey(group.label));
    items[group.label] = group.items.map(itemOrderKey);
    for (const item of group.items) {
      if (item.hidden) hidden.push(itemOrderKey(item));
      if (item.folderKey) {
        folders.push({ key: item.folderKey, label: item.name, group: group.label });
      }
      if (item.subItems) {
        subItems[subItemOrderKey(group.label, item)] = item.subItems.map((subItem) => subItem.path);
        for (const subItem of item.subItems) {
          if (subItem.hidden) hidden.push(subItem.path);
        }
      }
    }
  }
  return { groups: groups.map((group) => group.label), items, subItems, hidden, folders };
}

type SortableRowProps = {
  id: string;
  editing: boolean;
  className?: string;
  children: (handle: React.ReactNode) => React.ReactNode;
};

const SortableRow: React.FC<SortableRowProps> = ({ id, editing, className, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    disabled: !editing,
  });
  const handle = editing ? (
    <button
      type="button"
      className="mr-1 inline-flex w-5 shrink-0 cursor-grab touch-none items-center justify-center text-base leading-none text-gray-400 hover:text-brand-500 active:cursor-grabbing"
      aria-label="순서 이동"
      title="잡아서 순서 이동"
      {...attributes}
      {...listeners}
    >
      ⠿
    </button>
  ) : null;
  return (
    <div
      ref={setNodeRef}
      className={`${className ?? ""} ${isDragging ? "relative z-10 opacity-60" : ""}`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
    >
      {children(handle)}
    </div>
  );
};

/** 순서 편집 중에만 뜨는 작은 조작 버튼(숨김·묶음)의 공통 스타일 */
const EDIT_ACTION_BTN =
  "shrink-0 rounded px-1 text-[11px] leading-none text-gray-400 transition-colors hover:text-brand-500";

const sortableId = (kind: "group" | "item" | "sub", scope: string, key: string) =>
  `${kind}:${encodeURIComponent(scope)}:${encodeURIComponent(key)}`;

function parseSortableId(id: string) {
  const [kind, scope, key] = id.split(":");
  if (!kind || scope == null || key == null) return null;
  return { kind, scope: decodeURIComponent(scope), key: decodeURIComponent(key) };
}

// 그룹 안에 item/subItem droppable이 중첩돼 있으므로 같은 층만 충돌 후보로 남긴다.
// 그렇지 않으면 그룹 손잡이를 그룹 제목에 놓아도 안쪽 item이 먼저 잡혀 이동이 무시된다.
const sidebarCollisionDetection: CollisionDetection = (args) => {
  const active = parseSortableId(String(args.active.id));
  if (!active) return [];
  const sameLayer = args.droppableContainers.filter((container) => {
    const candidate = parseSortableId(String(container.id));
    return container.id !== args.active.id && candidate?.kind === active.kind;
  });
  const filteredArgs = { ...args, droppableContainers: sameLayer };
  const directlyUnderPointer = pointerWithin(filteredArgs);
  return directlyUnderPointer.length > 0 ? directlyUnderPointer : closestCenter(filteredArgs);
};

function buildNavGroups(
  isAdmin: boolean,
  isGlobalAdmin: boolean,
  isStoreManager: boolean,
  can: (group: PageGroupKey) => boolean,
  isParentStore: boolean,
  canSeeInbound: boolean,
  canSeeStoreLeads: boolean,
  // 외부용 기능 스위치 화면을 볼 수 있나 — 헤더 [내부용|외부용] 탭과 같은 선
  // (전역 관리자 또는 라인업 매장 대표). canSeeInbound 와 판정식이 같지만 뜻이 다르므로
  // 재사용하지 않는다 — 한쪽 기준이 바뀔 때 조용히 따라 바뀌면 안 된다.
  canSeeEditionAdmin: boolean,
  counselUnansweredCount = 0,
  // 과거 상담 업로드 메뉴 — 매장 대표 이상(서버 requireCounselAdmin 과 같은 판정).
  canCounselArchive = false,
  // 중고폰 보상은 대표 전용 매장 관리와 달리, 매장에 소속된 직원도 함께 접수·가격표를 다룬다.
  hasStoreMembership = false,
  // 신분증 스캔을 개통 CRM 메뉴에 올리나 — 전역 스위치(외부 기능 토글)가 켜져 있을 때.
  // CRM 그룹째 권한으로 걸러지므로 «CRM 이 보이면 스캔도 보인다»(shared/account-areas.ts scanInFullMenu).
  showIdCardScan = false,
): NavGroup[] {
  const groups: NavGroup[] = [
    {
      label: "개통 CRM",
      items: [
        { icon: <CustomerIcon />, name: "고객 관리", path: "/crm/customers" },
        {
          icon: <CustomerIcon />,
          name: "가망고객",
          path: "/crm/prospects",
        },
        {
          icon: <SettlementIcon />,
          name: "개통완료",
          path: "/crm/activations-complete",
        },
        {
          icon: <SettlementIcon />,
          name: "정산완료",
          path: "/crm/settlements-complete",
        },
        {
          icon: <ArchiveIcon />,
          name: "CS 및 단순문의 고객",
          path: "/crm/archived-applications",
        },
        {
          icon: <ArchiveIcon />,
          // 옛 폼변환기 기록 아카이브 — 이름·폰번호로 과거 개통 조건을 확인
          name: "옛 개통이력",
          new: true,
          path: "/crm/legacy-activations",
        },
        {
          icon: <ArchiveIcon />,
          // 법정 보관기간이 지난 고객 정보가 언제 지워지는지 보는 자리. 파기 전에 매장이
          // 직접 내려받을 수 있어야 해서 CRM 묶음에 둔다(관리자 묶음이 아니다).
          name: "개인정보 파기 예정",
          new: true,
          path: "/privacy/destruction",
        },
        ...(isStoreManager
          ? [
              {
                icon: <SettlementIcon />,
                // 월별 금액 마감 현황판 — 매장대표에게만 노출(스크린챗 #347).
                name: "정산관리",
                new: true,
                path: "/crm/settlements",
              },
            ]
          : []),
        {
          icon: <CallRecordingIcon />,
          // 통화 녹음 업로드 → 자동 텍스트 변환·요약·할 일 정리, 상담 통화는 고객에 연결
          name: "통화 녹음 정리",
          new: true,
          path: "/crm/call-recordings",
        },
        // 신분증 스캔 — 고객 등록의 입구라 서류 자동작성 앞에 둔다. 예전엔 선택 이용(스캔 전용)
        // 매장에만 보였는데, 스캐너 캠페인 고객에게 CRM 전체를 열면서 여기에도 올렸다(2026-09-30).
        ...(showIdCardScan
          ? [{ icon: <FormTemplateIcon />, name: "신분증 스캔", path: "/tools/id-card-scan" }]
          : []),
        {
          icon: <FormTemplateIcon />,
          name: "서류 자동작성",
          path: "/crm/form-templates",
        },
        {
          icon: <DealerTemplateIcon />,
          name: "텍스트 자동변환",
          path: "/crm/dealer-templates",
        },
        {
          icon: <ScheduledMessageIcon />,
          // 고객 문자 발송 — 매일 여는 캘린더 → 즉석 예약 → 자동 규칙 설정 → 결과(내역) 순.
          // 사용빈도(매일→가끔)와 업무 흐름(보내기→설정→확인)을 함께 따른 배치.
          name: "문자 발송",
          new: true,
          subItems: [
            { name: "문자 보내기", path: "/crm/messages/compose", new: true },
            { name: "오늘 발송", path: "/crm/messages/today", new: true },
            { name: "발송 캘린더", path: "/crm/messages/calendar", new: true },
            { name: "직접 예약", path: "/crm/scheduled-messages", new: true },
            { name: "발송 규칙", path: "/crm/messages/rules", new: true },
            { name: "전송 내역", path: "/crm/messages/history", new: true },
            ...(canSeeInbound
              ? [{ name: "수신 내역", path: "/admin/inbound-messages", new: true }]
              : []),
          ],
        },
        {
          icon: <PaybackIcon />,
          // 고객에게 돌려줄 페이백 대상 지정 → 링크 발급(계좌 직접입력) → 지급완료 관리 (task 405)
          name: "페이백 관리",
          new: true,
          path: "/crm/payback",
        },
        {
          icon: <PolicySettingsIcon />,
          // 관리자가 가끔 들어가는 폼 항목 설정·데이터 입출력 묶음 — 평소엔 접어둔다
          name: "설정 및 데이터",
          subItems: [
            // 신청서가 언제 얼마나 만들어졌는지 일별/주별 누적 차트로 (고객구분별)
            { name: "신청서 추이", path: "/crm/application-trends" },
            // 고객 입력폼·신청서에 노출할 항목(필드)·그룹 구성을 정의
            { name: "신청서 항목 관리", path: "/crm/customer-form-settings" },
            // 고객 상태 파이프라인의 가운데 단계 추가·이름·색·순서·삭제 (task 312)
            { name: "단계 관리", path: "/crm/pipeline-stages-settings" },
            // 업체 원장(2026-06-12) — 대리점·재고 거래처·정책 도매상까지 묶는 화면
            { name: "업체 관리", path: "/policy/vendors" },
            // 고객·신청서 데이터를 파일로 추출
            { name: "데이터 내보내기", path: "/crm/export" },
            // 형식 없는 엑셀을 접수 → 담당자가 분석해 옛 개통이력에 반영 (2026-07-05 개편)
            { name: "데이터 가져오기", path: "/crm/import" },
            // 특정 시점으로 되돌릴 수 있게 DB 전체를 백업해두는 체크포인트(최대 10개)
            { name: "데이터 복원", path: "/crm/restore" },
            // 휴지통 — 삭제된 신청서를 모아 보고 복구 (자주 안 쓰는 정리용이라 설정 안으로)
            { name: "삭제한 신청서", path: "/crm/deleted-applications" },
          ],
        },
      ],
    },
    {
      label: "정책관리",
      items: [
        {
          icon: <PolicyPipelineIcon />,
          // 액면 업로드 · 업로드 내역 · 구두 업로드를 한 페이지에서 (2026-06-15 통합)
          name: "정책 자동화",
          path: "/policy/pipeline",
        },
        {
          icon: <PolicySettingsIcon />,
          // 업체 등록 · 업체가 안 붙어 어느 목록에도 안 잡히던 파일 지정 · 업체별 읽는 법(파서) 상태.
          // 미분류 파일을 '발견'하는 게 목적이라 접히는 묶음에 넣지 않고 항상 보이게 둔다.
          name: "업체·파서 현황",
          new: true,
          path: "/policy/vendor-status",
        },
        {
          icon: <PolicyCalendarIcon />,
          // 정책 적용기간을 가로 타임라인(간트)으로 보고 막대를 끌어 기간을 조정
          name: "정책 캘린더",
          path: "/policy/calendar",
        },
        {
          icon: <PolicyBoardIcon />,
          // 통신사×요금제군×업체를 한 매트릭스로 펼친 전체 리베표 (구 "전체 리베표")
          name: "리베이트 합산",
          path: "/policy/rebate-matrix",
        },
        {
          icon: <PolicyBoardIcon />,
          // 표준화 이전 원문 축으로 그려 추출한 행이 하나도 안 빠지는 표 (리드 시연용)
          name: "전체 리베이트 표",
          new: true,
          path: "/policy/rebate-matrix-full",
        },
        {
          icon: <PolicyQuoteSheetIcon />,
          // 각 모델·요금제에서 업체 중 최고 리베이트만 뽑은 고객 안내용 최고 시세표
          // 예전 단일 링크의 경로를 orderKey로 유지해 매장이 저장한 메뉴 순서·숨김 설정을 승계한다.
          name: "시세표",
          orderKey: "/policy/quote-sheet",
          subItems: [
            { name: "시세표 작성하기", path: "/policy/quote-sheet" },
            { name: "시세표 제작모드", path: "/policy/quote-sheet-builder", new: true },
            { name: "내 시세표 틀", path: "/policy/quote-templates", new: true },
          ],
        },
        {
          icon: <PolicyModelGroupsIcon />,
          // 업체(도매상)별 구분 그룹을 한 페이지에서 만들고·복제하고·AI로 채운다 (구 파이프라인 "구분 그룹 관리" 버튼 승격)
          name: "업체별 모델 그룹 관리",
          path: "/policy/model-groups",
        },
        {
          icon: <PolicySettingsIcon />,
          // 개발자·관리자가 기준 데이터를 점검하는 묶음 — 평소엔 접어두고 필요할 때 펼친다
          name: "정책 설정",
          subItems: [
            // 업체별로 그동안 올린 액면·구두 업로드를 한눈에 보고 수정·삭제하는 대장
            { name: "정책 업로드 내역", path: "/policy/uploads" },
            // 고객 화면에 실제 반영한 순간의 긴 이미지 한 장·엑셀을 다시 받는 영구 보관함 (#626)
            { name: "내보낸 시세표 보관함", path: "/policy/quote-exports" },
            // 올린 정책표 엑셀 원본을 업체·날짜로 찾아 그대로 다시 내려받는 영구 보관함 (티켓 #9)
            { name: "정책표 원본 보관함", path: "/policy/archive" },
            // 통신사 공시지원금이 어제 대비 달라진 내역(일/주 단위, 함께 움직인 모델 묶음). task 335
            { name: "공시지원금 변동", path: "/policy/subsidy-changes" },
            // 표준 모델 사전 — 신모델 유입 이력(날짜별)·확인 도장·공시/정책표 연결. 모든 역할(2026-09-05)
            { name: "모델 관리", path: "/policy/models" },
            // 도매상 표기 → 표준 단말기·구간 별칭(공통/업체별) 추가·수정·삭제
            { name: "모델명 요금제 매칭", path: "/policy/aliases" },
            // 영역 지정 AI 가 참고하는 규칙(승인·중지) + 업체별 AI 성적·수정 이력 (2026-09-18)
            { name: "영역 지정 AI 학습", path: "/policy/region-learning" },
            // 요금제별 구간(군) 소속 전체 열람 — 리베 조회·비교표의 구간 매칭 기준
            { name: "요금제 구간표", path: "/policy/plan-groups" },
            // 통신사 기준 구간 → 요금제 → 대리점별 군을 한 표로 관리(2026-09-05 미팅). 대리점 구간표 확인·확정의 주 작업 화면
            { name: "요금제군 한눈에", path: "/policy/plan-group-matrix" },
            // 업로드별 시계열 이상 신호(평소와 다른 점) 관측 — 이상탐지 1단계
            { name: "이상탐지 관측", path: "/policy/anomalies" },
            // 다른 줄이 같은 모델 칸에 다른 금액을 넣은 곳 — 모델 지정을 고치거나 «고칠 것 없음» 확정(티켓 #993 후속)
            { name: "원문 충돌 검토", path: "/policy/conflict-review" },
            // 데이터 카탈로그 — 매장대표(owner)에게 여기 노출(통신사 이름·판매 범위 편집). 본사 관리자는
            // 아래 "관리자" 그룹에서 보고, 매장직원은 안 보인다.
            ...(isStoreManager
              ? [{ name: "데이터 카탈로그", path: "/policy/catalog" }]
              : []),
          ],
        },
      ],
    },
    {
      label: "재고관리",
      items: [
        {
          icon: <InventoryDeviceIcon />,
          name: "단말기관리",
          path: "/inventory/devices",
        },
        {
          icon: <InventoryHoldingsIcon />,
          name: "보유현황",
          path: "/inventory/holdings",
        },
      ],
    },
    {
      // 매장 공용 파일·링크 보관함 (스크린챗 #87). RBAC 그룹에 안 묶는다 — 자료함은 CRM·정책·재고
      // 어느 업무에도 딸리지 않은 매장 살림이고, 상단 통합검색창이 전 화면에서 이 자료를 연다.
      label: "업무 자료함",
      items: [
        {
          icon: <LibraryIcon />,
          // 대리점 가입 URL·엑셀·메모장·압축파일을 한곳에 모아두고 이름으로 찾아 바로 연다
          name: "업무 자료함",
          new: true,
          path: "/library",
        },
        ...(isStoreManager
          ? [
              {
                icon: <PolicyCalendarIcon />,
                // 대표 본인이 반복해서 챙길 일을 등록하고, 전 화면 상단 띠에서 완료한다 (#581).
                name: "정기 할 일",
                new: true,
                path: "/recurring-tasks",
              },
            ]
          : []),
      ],
    },
    {
      label: "카톡 상담",
      items: [
        {
          icon: <KakaoCounselIcon />,
          // 카톡 채널 문의를 상담 링크로 받아 AI 초안 + 사람 승인으로 응대
          name: "상담 인박스",
          path: "/kakao-counsel",
          badge: counselUnansweredCount,
        },
        // 과거 상담 내역 업로드 — 카톡 채널 내보내기(csv·zip)를 모아 두는 보관함. **모으기만 한다**
        // (학습은 나중에 정한다 — shared/counsel-chat-archive.ts). 학습 재료를 넣는 자리라 매장 대표
        // 이상에게만 보인다(서버 requireCounselAdmin 과 같은 판정 canAccessCounselTraining).
        ...(canCounselArchive
          ? [
              {
                icon: <KakaoCounselIcon />,
                name: "과거 상담 업로드",
                new: true,
                path: "/kakao-counsel/chat-archive",
              },
            ]
          : []),
        // 🗄️ 2026-08-22 아카이브 — "챗봇 훈련장"·"챗봇이 배운 것" 두 줄을 여기서 걷었다.
        //
        // 왜: 사장님이 **화면에서 챗봇을 가르치지 않기로** 했다. 챗봇은 매장이 이미 쓰고 있는
        // 안내 템플릿(기본안내사항·공지사항)을 근거로 답하고, 고칠 게 생기면 사장님이 직접 손본다.
        // 그런데 두 화면은 "사장님이 단계를 밟아 시스템을 채운다"는 전제로 만들어져 있어서,
        // 아무도 안 채우면 커버리지 잔소리만 남는다.
        //
        // 화면·API·서버 코드는 그대로 살아 있다 — 되살리려면 이 블록과 App.tsx 라우트 두 줄을
        // 되돌리면 된다(주소로 직접 들어가는 것도 App.tsx 에서 인박스로 넘긴다).
      ],
    },
  ];

  // RBAC: crm·policy·inventory 그룹은 권한 none이면 통째로 숨긴다.
  // permissions 미로딩이면 can()이 true → 기존처럼 보인다(규칙2). 관리자 그룹은 아래 isAdmin만 본다.
  const visibleGroups = groups.filter((g) => {
    const key = GROUP_LABEL_TO_KEY[g.label];
    return key ? can(key) : true;
  });

  if (isAdmin) {
    // 관리자 메뉴 — 접는 묶음 없이 한 단계로 평평하게 나열한다.
    // 회원 관리는 모든 admin(매장 소속 포함), 나머지 시스템 기능은 전역 관리자만 노출.
    const adminItems: NavItem[] = [
      { icon: <UsersAdminIcon />, name: "회원 관리", path: "/admin/users" },
      // 사용자 활동 로그 — 누가·언제·어디서·무엇을 했나(히트맵·인기 페이지·타임라인)
      { icon: <ActivityLogIcon />, name: "활동 로그", path: "/admin/activity" },
      { icon: <ActivityLogIcon />, name: "페이지 인벤토리", path: "/admin/page-inventory" },
      // 정책 데이터 큰 그림 — 업체 신선도·이상 신호 히트맵·AI 종합 브리핑 (admin 이상)
      { icon: <PolicyCatalogIcon />, name: "데이터 헬스", path: "/admin/data-health" },
      // 정책 미매핑·파싱이상 신호 집계 — admin 이상만(관찰 전용 개발자 콘솔, task 316)
      { icon: <PolicySettingsIcon />, name: "파싱이상 콘솔", path: "/policy/dev/anomalies" },
    ];

    // 시스템 기능 — 매장대표(매장 소속 admin)에겐 숨긴다. 전역 관리자만 노출.
    if (isGlobalAdmin) {
      adminItems.push(
        { icon: <TenantAdminIcon />, name: "매장 관리", path: "/admin/tenants" },
        // 매장의 대표·직원 명단 + 역할변경 + 개인 예외
        { icon: <UsersAdminIcon />, name: "매장별 권한", path: "/admin/store-members" },
        // 크롤링으로 모은 제로노트 5종 데이터를 열람·검색
        { icon: <PolicyCatalogIcon />, name: "데이터 카탈로그", path: "/policy/catalog" },
        // 크롤링 소스·실행 이력·변경 상세(diff) 운영
        { icon: <PolicyCatalogIcon />, name: "크롤링 관리", path: "/admin/crawling" },
        // 요금제 알라미 — 판매점 현황·기준일·고객·발송이력(공유 pa_ 테이블)
        { icon: <TenantAdminIcon />, name: "요금제 알라미", path: "/admin/plan-alarm" },
      );
      // 판매점 리드는 여기서 뺐다 — 라인업 매장 대표(role=owner)도 봐야 해서 아래 "판매점 영업"
      //   그룹으로 옮겼다(2026-07-28). 전역 관리자는 그 그룹으로 그대로 보인다.
    }


    visibleGroups.push({ label: "관리자", items: adminItems });
  }

  // 대표와 직원이 함께 쓰는 중고폰 보상. 대표 전용 「매장 관리」 묶음 바로 위에 둔다.
  if (hasStoreMembership) {
    visibleGroups.push({
      label: "중고폰 보상",
      items: [
        {
          icon: <InventoryDeviceIcon />,
          name: "중고폰 보상",
          new: true,
          path: "/used-phone-compensation",
        },
      ],
    });
  }

  // 매장대표(owner) — 본사 관리자 메뉴 대신 자기 매장 "직원 관리"만 노출한다.
  // (isAdmin 과 상호배타: owner 는 isAdmin=false 라 위 블록을 타지 않는다.)
  if (isStoreManager) {
    const storeItems: NavItem[] = [
      {
        icon: <UsersAdminIcon />,
        name: "직원 관리",
        path: "/store/staff",
      },
      {
        icon: <TenantAdminIcon />,
        name: "고객 사이트 관리",
        path: "/store/storefront",
      },
    ];
    // 상위 판매점(부모 매장 owner)만 — 자기 밑 직속 자식 매장을 만들고 관리하는 화면.
    // 자식/일반 매장은 isParentStore=false 라 안 보인다(기존 화면 불변).
    if (isParentStore) {
      storeItems.push({
        icon: <TenantAdminIcon />,
        name: "도도매 업체 관리",
        path: "/store/child-stores",
      });
    }
    // 커스텀 폼빌더 — 매장대표가 직접 만드는 수집 폼(고객·거래처에 링크로 배포) + 응답 관리.
    // 라우트/API 접근은 StoreManagerRoute·requireStoreManager 가 별도로 막는다(메뉴 숨김은 보조).
    storeItems.push({
      icon: <FormTemplateIcon />,
      name: "폼 관리",
      path: "/form-builder",
    });
    storeItems.push({
      icon: <ArchiveIcon />,
      name: "폼 응답",
      path: "/form-builder/responses",
    });
    visibleGroups.push({ label: "매장 관리", items: storeItems });
  }

  // 외부 배포판 — 전역 관리자 + 라인업 매장(admin-demo) 대표만.
  // 헤더 [내부용|외부용] 탭이 보이는 사람과 **같은 선**이다. "관리자" 그룹에 넣지 않는 이유는
  // 그 그룹 자체가 isAdmin(role=admin) 일 때만 그려져서 라인업 대표(owner)에겐 통째로 안
  // 보이기 때문이다 — 아래 판매점 영업이 같은 이유로 따로 서 있다.
  // 라우트/API 접근은 EditionAdminRoute / requireEditionAdmin 이 막는다(메뉴 숨김은 보조).
  if (canSeeEditionAdmin) {
    visibleGroups.push({
      label: "외부 배포판",
      items: [
        { icon: <TenantAdminIcon />, name: "선택 이용 기능", path: "/admin/external-features" },
      ],
    });
  }

  // 판매점 영업 — 전역 관리자 + 라인업 매장(admin-demo) 대표만.
  // 전국 판매점 원본 → 연락처 수집 → 오토콜/응답까지 도는 영업 파이프라인 화면.
  // 라우트/API 접근은 StoreLeadRoute / requireStoreLeadAccess 가 별도로 막는다(메뉴 숨김은 보조).
  if (canSeeStoreLeads) {
    visibleGroups.push({
      label: "판매점 영업",
      items: [{ icon: <CustomerIcon />, name: "판매점 리드", path: "/admin/store-leads" }],
    });
    // 사진 판독 결과함 — 판독기가 단가표 사진을 제대로 읽는지 원본과 나란히 보는 본사 실험실(2026-09-25).
    // 판매점 리드와 같은 사람만(본사 위임). 라우트는 같은 StoreLeadRoute, API 는 requireStoreLeadAccess.
    visibleGroups.push({
      label: "본사 도구",
      items: [{ icon: <TenantAdminIcon />, name: "사진 판독 결과함", path: "/admin/photo-read-lab" }],
    });
  }

  return visibleGroups;
}

/**
 * 도도매 업체 설정 창이 쓰는 메뉴 목록. 목록을 따로 복제하지 않고 실제 업체 owner 조건으로
 * buildNavGroups를 실행해 뽑으므로, 사이드바에 새 메뉴가 생기면 설정 창에도 자동으로 나타난다.
 */
const CHILD_STORE_MENU_WARNINGS: Record<string, string> = {
  "/crm/import": "업체가 엑셀로 올린 고객이 내 목록에 그대로 섞입니다",
  "/crm/restore": "업체가 되살린 신청서가 내 목록에 다시 나타납니다",
  "/crm/deleted-applications": "업체가 지운 신청서를 스스로 되살릴 수 있습니다",
  "/crm/pipeline-stages-settings": "업체가 만든 단계로 내 목록에 섞여 보입니다",
  "/crm/customer-form-settings": "업체가 바꾼 칸 구성이 내 화면에도 그대로 보입니다",
  "/store/storefront": "업체가 자기 손님용 사이트를 열 수 있습니다",
};

const CHILD_STORE_MENU_GROUP_NOTICES: Record<string, string> = {
  "정책관리":
    "이 묶음은 업체가 자기 정책표를 직접 올려야 채워집니다. 대표님 정책은 넘어가지 않습니다.",
};

export type ChildStoreMenuCatalogEntry = NavCatalogEntry & {
  warning?: string;
  groupNotice?: string;
};

export function buildChildStoreMenuCatalog(): ChildStoreMenuCatalogEntry[] {
  const groups = buildNavGroups(
    false,
    false,
    true,
    () => true,
    false,
    false,
    false,
    false,
    0,
    false,
    true,
  );
  const out: NavCatalogEntry[] = [];
  for (const group of groups) {
    for (const item of group.items) {
      if (item.path) {
        out.push({
          group: group.label,
          parent: null,
          name: item.name,
          path: item.path,
          visibility: "도도매 업체",
        });
      }
      for (const sub of item.subItems ?? []) {
        out.push({
          group: group.label,
          parent: item.name,
          name: sub.name,
          path: sub.path,
          visibility: "도도매 업체",
        });
      }
    }
  }
  return out.map((entry) => ({
    ...entry,
    warning: CHILD_STORE_MENU_WARNINGS[entry.path],
    groupNotice: CHILD_STORE_MENU_GROUP_NOTICES[entry.group],
  }));
}

/** 업체 tenant에 열린 경로만 남긴다. 폴더/상위메뉴는 열린 자식이 하나라도 있을 때만 남는다. */
function filterNavGroupsForChildStore(
  groups: NavGroup[],
  enabledPaths: readonly string[],
): NavGroup[] {
  const enabled = new Set(enabledPaths);
  return groups
    .map((group) => ({
      ...group,
      items: group.items
        .map((item) => {
          const subItems = item.subItems?.filter((sub) => enabled.has(sub.path));
          if (item.path && !enabled.has(item.path)) return null;
          if (!item.path && item.subItems && subItems?.length === 0) return null;
          return { ...item, ...(item.subItems ? { subItems } : {}) };
        })
        .filter((item): item is NavItem => item !== null),
    }))
    .filter((group) => group.items.length > 0);
}

/** LNB 메뉴 하나를 평탄화한 형태 (아이콘 제거 — 목록·집계용) */
export type NavCatalogEntry = {
  group: string;
  /** 부모 메뉴 이름 (서브메뉴일 때만) */
  parent: string | null;
  name: string;
  path: string;
  /** 이 메뉴가 보이려면 필요한 조건 — "안 쓰는 것"과 "못 보는 것"을 구분하는 근거 */
  visibility: string;
};

/**
 * LNB 전체 메뉴를 평탄한 목록으로 뽑는다 — 페이지 인벤토리(/admin/page-inventory)용.
 *
 * 권한 조합별로 buildNavGroups 를 여러 번 돌려 "누구에게 보이는 메뉴인지"까지 붙인다.
 * 별도 상수 목록을 두지 않는 이유: 메뉴는 buildNavGroups 가 SSOT 라 상수는 반드시 어긋난다
 * (메뉴 추가한 사람이 상수를 같이 고칠 거란 보장이 없고, 어긋나도 아무도 모른다).
 * 여기서 실시간 추출하면 새 메뉴가 인벤토리에 저절로 나타난다.
 */
export function buildNavCatalog(): NavCatalogEntry[] {
  const flatten = (groups: NavGroup[]): Omit<NavCatalogEntry, "visibility">[] => {
    const out: Omit<NavCatalogEntry, "visibility">[] = [];
    for (const g of groups) {
      for (const item of g.items) {
        if (item.path) out.push({ group: g.label, parent: null, name: item.name, path: item.path });
        for (const sub of item.subItems ?? []) {
          out.push({ group: g.label, parent: item.name, name: sub.name, path: sub.path });
        }
      }
    }
    return out;
  };

  const allOn = () => true;
  const allOff = () => false;
  // 좁은 권한부터 넓은 권한 순으로 훑어, 각 경로가 "처음 등장하는" 조합을 그 메뉴의 노출조건으로 본다.
  const probes: { visibility: string; groups: NavGroup[] }[] = [
    { visibility: "직원(전 사용자)", groups: buildNavGroups(false, false, false, allOn, false, false, false, false, 0, false, true) },
    { visibility: "매장대표", groups: buildNavGroups(false, false, true, allOn, false, false, false, false, 0, false, true) },
    { visibility: "매장대표(부모매장)", groups: buildNavGroups(false, false, true, allOn, true, false, false, false, 0, false, true) },
    { visibility: "본사 관리자", groups: buildNavGroups(true, false, false, allOn, false, false, false, false) },
    { visibility: "전역 관리자", groups: buildNavGroups(true, true, false, allOn, false, false, false, false) },
    // 라인업 매장 대표 예외로 열리는 메뉴들(메시지 수신·판매점 영업)
    { visibility: "라인업 대표 예외", groups: buildNavGroups(true, true, false, allOn, false, true, true, true) },
  ];

  const seen = new Map<string, NavCatalogEntry>();
  for (const probe of probes) {
    for (const entry of flatten(probe.groups)) {
      if (seen.has(entry.path)) continue;
      seen.set(entry.path, { ...entry, visibility: probe.visibility });
    }
  }

  // RBAC 그룹 권한이 꺼지면 사라지는 메뉴는 위 probe(권한 all-on)에만 잡힌다 —
  // 권한 all-off 로 한 번 더 돌려 "그룹 권한이 필요한 메뉴"를 표시해준다.
  const withoutGroupPerms = new Set(
    flatten(buildNavGroups(true, true, true, allOff, true, true, true, true, 0, false, true)).map((e) => e.path),
  );
  for (const [path, entry] of seen) {
    if (!withoutGroupPerms.has(path)) {
      seen.set(path, { ...entry, visibility: `${entry.visibility} · 그룹권한 필요` });
    }
  }

  return [...seen.values()];
}

/**
 * 화면 크기 조절 상자 (티켓 #57) — 로고 바로 아래.
 * +/− 로 화면 전체(글자·버튼·표)가 한 단계씩 커지고 작아진다. 값은 계정별로 저장된다
 * (동작·저장 위치 SSOT: src/context/UiZoomContext.tsx). 퍼센트 숫자를 누르면 100% 로 복귀.
 *
 * `compact` 는 옆에 순서 편집 버튼이 두 개로 늘어난 동안(편집 중)만 켜진다 — 그때만 "화면 크기"
 * 글자를 접어 한 줄에 다 들어가게 한다(폭 250px 고정).
 */
const ZoomControl: React.FC<{ compact?: boolean }> = ({ compact = false }) => {
  const { zoomPercent, zoomIn, zoomOut, resetZoom, canZoomIn, canZoomOut } = useUiZoom();

  const btn =
    "flex h-6 w-6 items-center justify-center rounded border border-gray-200 text-sm font-semibold leading-none text-gray-600 transition hover:bg-gray-100 disabled:opacity-40 disabled:hover:bg-transparent dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800";

  return (
    <div
      className="flex h-[38px] shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-800 dark:bg-gray-800/40"
      title="화면 크기 조절"
    >
      {!compact && (
        <span className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400">화면 크기</span>
      )}
      <div className="flex items-center gap-1">
        <button
          type="button"
          className={btn}
          onClick={zoomOut}
          disabled={!canZoomOut}
          aria-label="화면 작게"
          title="화면 작게"
        >
          −
        </button>
        <button
          type="button"
          className="min-w-[38px] rounded px-0.5 text-center text-xs tabular-nums text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          onClick={resetZoom}
          aria-label="화면 크기 기본값(100%)으로"
          title="눌러서 100%로 되돌리기"
        >
          {zoomPercent}%
        </button>
        <button
          type="button"
          className={btn}
          onClick={zoomIn}
          disabled={!canZoomIn}
          aria-label="화면 크게"
          title="화면 크게"
        >
          +
        </button>
      </div>
    </div>
  );
};

/** 「기기 설정」이 맨 앞이다 — 기본값이자 대부분이 그대로 두는 값이라 첫 칸에 온다. */
const THEME_CHOICES: Array<{ key: ThemePreference; label: string; icon: string }> = [
  { key: "auto", label: "기기 설정", icon: "🖥" },
  { key: "light", label: "라이트", icon: "☀️" },
  { key: "dark", label: "나이트", icon: "🌙" },
];

/**
 * 화면 모드 — ⚙️ 설정 안의 세 칸짜리 고르개 (2026-09-09).
 *
 * 그전까지 이 앱에서 나이트 모드를 바꾸는 길은 **상단 띠의 아이콘 하나**뿐이었고, 그건
 * «라이트냐 나이트냐»만 뒤집었다. 그래서 **«기기 설정을 따른다»는 상태 자체가 없었고**
 * 기본값이 라이트로 못 박혀 있어, 폰·맥을 밤 모드로 쓰는 사람도 이 앱만 낮 화면이었다.
 *
 * 도는 버튼 대신 세 칸을 다 펴 보인다 — «지금 어디인지»가 한눈에 보여야 하고, 특히
 * 기기 설정 상태에서는 지금 화면이 라이트라도 그게 «고른 라이트»가 아니라는 걸 알아야
 * 한다(그래야 밤이 되면 저절로 바뀌는 걸 고장으로 오해하지 않는다).
 * 상단 띠의 아이콘은 그대로 둔다 — 빠르게 한 번 뒤집는 자리는 그쪽이 낫다.
 */
const ThemeControl: React.FC = () => {
  const { theme, preference, chooseTheme } = useTheme();

  return (
    <>
      <div className="grid grid-cols-3 gap-1" role="group" aria-label="화면 모드">
        {THEME_CHOICES.map((choice) => {
          const active = preference === choice.key;
          return (
            <button
              key={choice.key}
              type="button"
              aria-pressed={active}
              onClick={() => chooseTheme(choice.key)}
              className={`rounded-lg border px-1 py-1.5 text-[11px] font-medium transition ${
                active
                  ? "border-brand-500 bg-brand-50 text-brand-600 dark:bg-brand-500/[0.12] dark:text-brand-400"
                  : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50 dark:border-gray-700 dark:bg-transparent dark:text-gray-300 dark:hover:bg-gray-800"
              }`}
            >
              <span aria-hidden className="block text-sm leading-none">
                {choice.icon}
              </span>
              <span className="mt-1 block">{choice.label}</span>
            </button>
          );
        })}
      </div>
      {/* 「기기 설정」일 때 지금 화면이 왜 이 색인지 말해 준다 — 안 적으면 밤에 저절로
          바뀌는 게 고장으로 읽힌다. */}
      <p className="mt-1.5 px-1 text-[10px] leading-tight text-gray-400">
        {preference === "auto"
          ? `지금은 ${theme === "dark" ? "나이트" : "라이트"} — 기기의 낮·밤 설정을 따라갑니다`
          : "이 브라우저에만 적용됩니다"}
      </p>
    </>
  );
};

/**
 * 왼쪽 메뉴 상단의 ⚙️ 설정 (2026-08-28).
 *
 * 예전엔 로고 아래 줄에 「화면 크기」 상자와 「순서 편집」 버튼이 나란히 널려 있었다. 둘 다
 * **가끔 한 번 만지는 설정**인데 늘 자리를 차지했고, 상단 띠의 햄버거(메뉴 펼침 고정)까지 합치면
 * "메뉴 자체를 손보는 조작"이 화면 두 곳에 흩어져 있었다. 셋을 이 한 칸에 모은다.
 *
 * ⚠️ **순서 편집은 여기서 시작만 하고 패널은 닫는다.** 편집 중에는 [완료]·[되돌리기]가 이 옆에
 *    그대로 서 있어야 한다 — 메뉴 안에 묻으면 편집 모드에서 빠져나올 길이 안 보인다.
 * ⚠️ 패널은 **사이드바 안에** 그린다(바깥에 그리면 마우스가 사이드바를 벗어난 것으로 판정돼
 *    메뉴가 좁은 띠로 접히면서 패널만 허공에 남는다).
 */
const SidebarSettingsPanel: React.FC<{
  pinned: boolean;
  onTogglePin: () => void;
  canEditOrder: boolean;
  onStartOrderEditing: () => void;
  onClose: () => void;
}> = ({ pinned, onTogglePin, canEditOrder, onStartOrderEditing, onClose }) => {
  const row =
    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs font-medium text-gray-600 transition hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800";

  return (
    <div
      role="dialog"
      aria-label="메뉴 설정"
      className="absolute left-4 top-[72px] z-[1] w-[230px] rounded-lg border border-gray-200 bg-white p-2 shadow-theme-lg dark:border-gray-800 dark:bg-gray-900"
    >
      <p className="px-2 pb-1 pt-0.5 text-[11px] font-semibold text-gray-400">화면 크기</p>
      <div className="px-1 pb-2">
        {/* compact — 상자 안의 '화면 크기' 글자는 접는다(바로 위 제목과 겹친다). */}
        <ZoomControl compact />
      </div>

      <div className="border-t border-gray-100 pt-1.5 dark:border-gray-800">
        <p className="px-2 pb-1 text-[11px] font-semibold text-gray-400">화면 모드</p>
        <div className="px-1 pb-2">
          <ThemeControl />
        </div>
      </div>

      <div className="border-t border-gray-100 pt-1 dark:border-gray-800">
        {/* 넓은 화면 전용 — 좁은 화면의 메뉴는 서랍이라 '고정'이라는 개념이 없다. */}
        <button type="button" onClick={onTogglePin} className={`${row} hidden xl:flex`}>
          <span aria-hidden>{pinned ? "📌" : "📍"}</span>
          {pinned ? "메뉴 고정 해제" : "메뉴 펼친 채 고정"}
        </button>

        {canEditOrder && (
          <button
            type="button"
            onClick={() => {
              onStartOrderEditing();
              onClose();
            }}
            className={`${row} hidden xl:flex`}
            title="메뉴 순서를 바꾸거나 숨기고, 묶음을 만듭니다"
          >
            <span aria-hidden>✏️</span>
            메뉴 순서 편집
          </button>
        )}

        {/* 개인정보 가리기 — 상단 띠의 눈 아이콘을 여기로 옮겼다(2026-08-28).
            누르면 그 자리에서 가려지므로 패널은 닫는다. 되돌리는 '가리기 해제'는 띠에 뜬다. */}
        <div onClick={onClose}>
          <PrivacyUnlockButton place="menu" className={row} />
        </div>
      </div>
    </div>
  );
};

const AppSidebar: React.FC = () => {
  const {
    isExpanded,
    isMobileOpen,
    isHovered,
    setIsHovered,
    setIsMobileOpen,
    toggleSidebar,
  } = useSidebar();
  // ⚙️ 설정 패널이 열려 있나 (화면 크기·메뉴 고정·순서 편집)
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const location = useLocation();
  useEffect(() => {
    if (isMobileOpen) {
      setIsMobileOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const { isAdmin, isGlobalAdmin, isStoreManager, isParentStore, isLead, can, hasHqDelegation, profile } =
    useAuth();
  const { edition, enabledFeatures } = useEdition();
  const isLeadUser = isLead();
  const isAdminUser = isAdmin();
  const isGlobalAdminUser = isGlobalAdmin();
  const hqDelegated = hasHqDelegation();
  const isStoreManagerUser = isStoreManager();
  const isParentStoreUser = isParentStore();
  // 수신 메시지: 전역 관리자거나 라인업 매장(admin-demo) 대표(owner)면 노출. InboundMessagesRoute 와 동일 판정.
  const canSeeInbound =
    isGlobalAdminUser || (profile?.role === "owner" && profile?.tenantId === "admin-demo");
  // 판매점 리드 · 선택 이용 매장 기능 스위치 — 둘 다 **본사 위임**으로 판정한다(전역 관리자이거나
  // hq_shared 쓰기를 받은 사람). 라우트 가드(StoreLeadRoute·EditionAdminRoute)·백엔드
  // hasHqDelegation 과 **같은 선**이어야 한다. 세 곳 중 하나만 어긋나면 "메뉴는 보이는데
  // 열면 튕김" 또는 "메뉴는 없는데 주소 치면 열림" 이 된다(rbac §14 의 지문).
  const canSeeStoreLeads = hqDelegated;
  const canSeeEditionAdmin = hqDelegated;
  const canSeeCounsel = !isLeadUser && can("kakao_counsel");
  // "지금 이 사람이 어느 매장을 보고 있나" — 이 파일의 **단 하나의 기준**이다.
  //
  // ⚠️ 예전엔 같은 판정이 세 갈래였다: 어떤 줄은 `tenantId` 만, 어떤 줄은 `tenantId || viewingTenantId`,
  //    메뉴 노출은 `tenantId != null`. 그래서 본사 관리자가 매장을 "임시 보기"로 들어가 있으면
  //    서버는 그 매장으로 통과시키는데 화면 판정만 어긋나는 일이 생겼다.
  //    서버(getTenantId, v2/routes/crm/_shared.ts)의 순서를 그대로 따른다 —
  //    **viewingTenantId(임시 보기) 가 먼저, 없으면 profiles.tenantId.**
  //    서버가 프로필 행을 직접 읽는 것과 달리 화면은 로그인 때 받은 프로필만 볼 수 있으므로,
  //    프로필에 안 실리는 소속(멤버십 등)까지는 화면에서 알 수 없다 — 그런 계정은 메뉴가 안 보여도
  //    주소로 들어오면 서버가 통과시키고 화면의 안내문도 뜨지 않는다(과다 차단이 아니라 과소 노출).
  const effectiveTenantId = profile?.viewingTenantId ?? profile?.tenantId ?? null;
  const hasCounselTenant = effectiveTenantId != null;
  // 과거 상담 업로드 메뉴 — 서버 관문(requireCounselAdmin)과 같은 함수로 판정한다. 화면엔 보이는데
  // 눌러 보면 403 이 나는 어긋남을 막는 유일한 방법이 같은 함수를 쓰는 것이다.
  const canCounselArchive =
    canSeeCounsel &&
    hasCounselTenant &&
    canAccessCounselTraining(profile?.role ?? "", true, profile?.permissions);
  // 도도매 업체는 상위 매장이 상담 메뉴를 켜 줬을 때만 배지를 묻는다 — 꺼진 업체가 1분마다
  // 물으면 서버가 매번 403 을 내 로그인 풀림 경보에 섞였다(2026-09, 4명이 1천여 건).
  const childStoreCounselEnabled =
    profile?.childStoreMenuPaths == null ||
    profile.childStoreMenuPaths.includes(CHILD_STORE_COUNSEL_MENU_PATH);
  const unansweredCountQuery = useCounselUnansweredCount(
    canSeeCounsel && hasCounselTenant && childStoreCounselEnabled,
  );
  const counselUnansweredCount =
    unansweredCountQuery.data?.unansweredConversations ??
    unansweredCountQuery.data?.pendingDrafts ??
    0;
  // 🗄️ 2026-08-22 — 챗봇 훈련장 메뉴 노출 판정(canCounselTraining)을 여기서 걷었다.
  // 훈련장·배운 것 메뉴 자체가 아카이브돼 판정할 대상이 없다. 서버 권한(canAccessCounselTraining)과
  // API 는 그대로 살아 있으므로, 메뉴를 되살릴 땐 이 계산과 아래 인자 전달을 함께 복원한다.
  const baseNavGroups = useMemo<NavGroup[]>(() => {
    // 선택 이용 매장 화면일 때는 평소 메뉴를 통째로 갈아끼운다 — 카탈로그의 진본은
    // shared/external-edition.ts 이고, 그중 무엇을 켤지는 관리자가 DB 로 정한다.
    // 권한(can)으로 거르지 않는 이유는 리드 시연과 같다: 체험의 기준은 "이 계정이 무엇을 할 수
    // 있나"가 아니라 "계약 전에 무엇까지 보여줄까"라서, 권한과 축이 아예 다르다.
    // ⚠️ 여기서 감추는 것만으로는 부족하다(주소를 직접 치면 열린다) — 실제 차단은
    //    ExternalEditionGate 가 하고, 둘 다 같은 목록을 본다.
    if (edition === "partial") {
      return [
        {
          label: EXTERNAL_NAV_GROUP_LABEL,
          items: enabledExternalFeatures(enabledFeatures).map((f) => ({
            name: f.name,
            icon: <LibraryIcon />,
            path: f.path,
          })),
        },
      ];
    }
    // 리드 시연 계정은 평소 메뉴 대신 시연 화면만 본다 — 목록의 진본은 lib/leadDemo.ts.
    // 권한(can)으로 거르지 않고 통째로 갈아끼우는 이유: 리드는 모든 그룹이 none 이라
    // 평소 경로로는 메뉴가 한 줄도 안 남는다.
    if (isLeadUser) {
      return [
        {
          label: LEAD_DEMO_NAV.groupLabel,
          items: LEAD_DEMO_NAV.items.map((it) => ({
            name: it.name,
            icon: <PolicyBoardIcon />,
            path: it.path,
          })),
        },
      ];
    }
    const groups = buildNavGroups(
      isAdminUser,
      isGlobalAdminUser,
      isStoreManagerUser,
      can,
      isParentStoreUser,
      canSeeInbound,
      canSeeStoreLeads,
      canSeeEditionAdmin,
      counselUnansweredCount,
      canCounselArchive,
      effectiveTenantId != null,
      enabledFeatures.includes(SCAN_AREA_KEY),
    );
    return profile?.childStoreMenuPaths == null
      ? groups
      : filterNavGroupsForChildStore(groups, profile.childStoreMenuPaths);
  }, [edition, enabledFeatures, isLeadUser, isAdminUser, isGlobalAdminUser, isStoreManagerUser, can, isParentStoreUser, canSeeInbound, canSeeStoreLeads, canSeeEditionAdmin, counselUnansweredCount, canCounselArchive, effectiveTenantId, profile?.childStoreMenuPaths]);

  const [savedOrder, setSavedOrder] = useState<SidebarOrderPreference | null>(null);
  const [orderLoaded, setOrderLoaded] = useState(false);
  const [isEditingOrder, setIsEditingOrder] = useState(false);
  const [draftGroups, setDraftGroups] = useState<NavGroup[] | null>(null);
  const [resetToDefault, setResetToDefault] = useState(false);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [orderError, setOrderError] = useState<string | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  useEffect(() => {
    let cancelled = false;
    setOrderLoaded(false);
    setSavedOrder(null);
    // 매장 소속 판정은 위 effectiveTenantId 한 곳만 본다(기준이 갈리면 메뉴 순서가 계정마다 다르게 뜬다).
    if (!effectiveTenantId) {
      setOrderLoaded(true);
      return () => {
        cancelled = true;
      };
    }
    fetchSidebarOrder()
      .then((order) => {
        if (!cancelled) setSavedOrder(order);
      })
      .catch(() => {
        if (!cancelled) setSavedOrder(null);
      })
      .finally(() => {
        if (!cancelled) setOrderLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [effectiveTenantId]);

  const orderedNavGroups = useMemo(
    () => applySidebarOrder(baseNavGroups, savedOrder),
    [baseNavGroups, savedOrder],
  );
  const navGroups = isEditingOrder && draftGroups ? draftGroups : orderedNavGroups;
  // 숨긴 그룹은 편집 중에만 흐리게 남아 되돌릴 수 있고, 평소 화면에서는 아예 빠진다.
  const visibleNavGroups = navGroups.filter((group) => isEditingOrder || !group.hidden);

  const discardOrderDraft = () => {
    setIsEditingOrder(false);
    setDraftGroups(null);
    setResetToDefault(false);
    setOrderError(null);
    setNewFolderFor(null);
    setNewFolderLabel("");
    setRenamingFolder(null);
  };

  // 완료 전에 다른 화면으로 이동하면 초안을 버린다. 저장된 순서는 그대로 유지된다.
  useEffect(() => {
    if (!isEditingOrder) return;
    discardOrderDraft();
    // pathname 이 바뀐 순간만 취소하며 편집 시작 자체에는 반응하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const startOrderEditing = () => {
    setDraftGroups(
      orderedNavGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          subItems: item.subItems?.map((subItem) => ({ ...subItem })),
        })),
      })),
    );
    setResetToDefault(false);
    setOrderError(null);
    setIsEditingOrder(true);
  };

  const restoreDefaultOrder = () => {
    setDraftGroups(
      baseNavGroups.map((group) => ({
        ...group,
        items: group.items.map((item) => ({
          ...item,
          subItems: item.subItems?.map((subItem) => ({ ...subItem })),
        })),
      })),
    );
    setResetToDefault(true);
    setOrderError(null);
  };

  // ─── 편집 중 조작: 숨기기 · 하위묶음 만들기/넣기/빼기 ─────────────────
  // 전부 draftGroups(초안)만 건드리고, 실제 저장은 "완료"를 눌러 finishOrderEditing 이 한다.

  /** 묶음 밖으로 뺀 메뉴에 아이콘을 되돌려주기 위한 경로→아이콘 표(원본 메뉴가 진본). */
  const iconByPath = useMemo(() => {
    const map = new Map<string, React.ReactNode>();
    for (const group of baseNavGroups) {
      for (const item of group.items) {
        if (item.path) map.set(item.path, item.icon);
      }
    }
    return map;
  }, [baseNavGroups]);

  const folderSeq = useRef(0);
  const [newFolderFor, setNewFolderFor] = useState<string | null>(null);
  const [newFolderLabel, setNewFolderLabel] = useState("");
  const [renamingFolder, setRenamingFolder] = useState<{ key: string; label: string } | null>(null);

  const mutateDraft = (mutate: (groups: NavGroup[]) => NavGroup[]) => {
    setResetToDefault(false);
    setDraftGroups((current) => (current ? mutate(current) : current));
  };

  const mapGroup = (groupLabel: string, mutate: (group: NavGroup) => NavGroup) =>
    mutateDraft((groups) =>
      groups.map((group) => (group.label === groupLabel ? mutate(group) : group)),
    );

  const toggleGroupHidden = (groupLabel: string) =>
    mapGroup(groupLabel, (group) => ({ ...group, hidden: !group.hidden }));

  const toggleItemHidden = (groupLabel: string, itemKey: string) =>
    mapGroup(groupLabel, (group) => ({
      ...group,
      items: group.items.map((item) =>
        itemOrderKey(item) === itemKey ? { ...item, hidden: !item.hidden } : item,
      ),
    }));

  const toggleSubHidden = (groupLabel: string, parentKey: string, path: string) =>
    mapGroup(groupLabel, (group) => ({
      ...group,
      items: group.items.map((item) =>
        itemOrderKey(item) !== parentKey || !item.subItems
          ? item
          : {
              ...item,
              subItems: item.subItems.map((subItem) =>
                subItem.path === path ? { ...subItem, hidden: !subItem.hidden } : subItem,
              ),
            },
      ),
    }));

  const createFolder = (groupLabel: string, label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    folderSeq.current += 1;
    const key = `${FOLDER_KEY_PREFIX}${Date.now().toString(36)}${folderSeq.current}`;
    mapGroup(groupLabel, (group) => ({
      ...group,
      items: [...group.items, { name: trimmed, icon: <FolderIcon />, folderKey: key, subItems: [] }],
    }));
    setNewFolderFor(null);
    setNewFolderLabel("");
  };

  const renameFolder = (groupLabel: string, folderKey: string, label: string) => {
    const trimmed = label.trim();
    if (!trimmed) return;
    mapGroup(groupLabel, (group) => ({
      ...group,
      items: group.items.map((item) =>
        item.folderKey === folderKey ? { ...item, name: trimmed } : item,
      ),
    }));
    setRenamingFolder(null);
  };

  /** 묶음을 없앤다 — 안에 있던 메뉴는 사라지지 않고 그 자리에 최상위로 돌아온다. */
  const dissolveFolder = (groupLabel: string, folderKey: string) =>
    mapGroup(groupLabel, (group) => {
      const index = group.items.findIndex((item) => item.folderKey === folderKey);
      if (index < 0) return group;
      const folder = group.items[index];
      const released: NavItem[] = (folder.subItems ?? []).map((subItem) => ({
        name: subItem.name,
        icon: iconByPath.get(subItem.path) ?? <ArchiveIcon />,
        path: subItem.path,
        new: subItem.new,
        hidden: subItem.hidden,
      }));
      return {
        ...group,
        items: [...group.items.slice(0, index), ...released, ...group.items.slice(index + 1)],
      };
    });

  const moveIntoFolder = (groupLabel: string, itemKey: string, folderKey: string) =>
    mapGroup(groupLabel, (group) => {
      const moving = group.items.find((item) => itemOrderKey(item) === itemKey);
      // 하위메뉴를 가진 메뉴와 묶음 자신은 다른 묶음에 넣지 않는다(3단 중첩 방지).
      if (!moving?.path || moving.subItems || moving.folderKey) return group;
      return {
        ...group,
        items: group.items
          .filter((item) => itemOrderKey(item) !== itemKey)
          .map((item) =>
            item.folderKey !== folderKey
              ? item
              : {
                  ...item,
                  subItems: [
                    ...(item.subItems ?? []),
                    {
                      name: moving.name,
                      path: moving.path as string,
                      new: moving.new,
                      hidden: moving.hidden,
                    },
                  ],
                },
          ),
      };
    });

  const moveOutOfFolder = (groupLabel: string, folderKey: string, path: string) =>
    mapGroup(groupLabel, (group) => {
      const index = group.items.findIndex((item) => item.folderKey === folderKey);
      if (index < 0) return group;
      const folder = group.items[index];
      const leaving = folder.subItems?.find((subItem) => subItem.path === path);
      if (!leaving) return group;
      const restored: NavItem = {
        name: leaving.name,
        icon: iconByPath.get(path) ?? <ArchiveIcon />,
        path,
        new: leaving.new,
        hidden: leaving.hidden,
      };
      const trimmedFolder: NavItem = {
        ...folder,
        subItems: (folder.subItems ?? []).filter((subItem) => subItem.path !== path),
      };
      // 빠져나온 메뉴는 그 묶음 바로 아래에 놓는다 — 방금 뭘 뺐는지 눈에 보이게.
      return {
        ...group,
        items: [
          ...group.items.slice(0, index),
          trimmedFolder,
          restored,
          ...group.items.slice(index + 1),
        ],
      };
    });

  const finishOrderEditing = async () => {
    if (!draftGroups || isSavingOrder) return;
    setIsSavingOrder(true);
    setOrderError(null);
    try {
      const saved = await updateSidebarOrder(
        resetToDefault ? null : serializeSidebarOrder(draftGroups),
      );
      setSavedOrder(saved);
      setIsEditingOrder(false);
      setDraftGroups(null);
      setResetToDefault(false);
    } catch (error) {
      setOrderError(error instanceof Error ? error.message : "메뉴 순서를 저장하지 못했습니다.");
    } finally {
      setIsSavingOrder(false);
    }
  };

  const handleOrderDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;
    const from = parseSortableId(String(active.id));
    const to = parseSortableId(String(over.id));
    // 같은 층·같은 묶음 안에서만 이동한다. 다른 그룹에 놓으면 아무 변화도 주지 않는다.
    if (!from || !to || from.kind !== to.kind || from.scope !== to.scope) return;
    setResetToDefault(false);
    setDraftGroups((current) => {
      if (!current) return current;
      if (from.kind === "group") {
        const oldIndex = current.findIndex((group) => group.label === from.key);
        const newIndex = current.findIndex((group) => group.label === to.key);
        return oldIndex < 0 || newIndex < 0 ? current : arrayMove(current, oldIndex, newIndex);
      }
      if (from.kind === "item") {
        return current.map((group) => {
          if (group.label !== from.scope) return group;
          const oldIndex = group.items.findIndex((item) => itemOrderKey(item) === from.key);
          const newIndex = group.items.findIndex((item) => itemOrderKey(item) === to.key);
          return oldIndex < 0 || newIndex < 0
            ? group
            : { ...group, items: arrayMove(group.items, oldIndex, newIndex) };
        });
      }
      if (from.kind === "sub") {
        const [groupLabel, parentKey] = from.scope.split(ORDER_KEY_SEPARATOR);
        return current.map((group) =>
          group.label !== groupLabel
            ? group
            : {
                ...group,
                items: group.items.map((item) => {
                  if (itemOrderKey(item) !== parentKey || !item.subItems) return item;
                  const oldIndex = item.subItems.findIndex((subItem) => subItem.path === from.key);
                  const newIndex = item.subItems.findIndex((subItem) => subItem.path === to.key);
                  return oldIndex < 0 || newIndex < 0
                    ? item
                    : { ...item, subItems: arrayMove(item.subItems, oldIndex, newIndex) };
                }),
              },
        );
      }
      return current;
    });
  };

  const [openSubmenu, setOpenSubmenu] = useState<{
    groupLabel: string;
    itemKey: string;
  } | null>(null);
  const [subMenuHeight, setSubMenuHeight] = useState<Record<string, number>>(
    {}
  );
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) => location.pathname === path,
    [location.pathname]
  );

  useEffect(() => {
    // 편집 중에는 손대지 않는다 — 초안이 바뀔 때마다(메뉴를 묶음에 넣거나 순서를 바꿀 때마다)
    // 지금 펼쳐 둔 묶음이 닫혀 버리면 편집이 불가능해진다.
    if (isEditingOrder) return;
    let submenuMatched = false;
    navGroups.forEach((group) => {
      group.items.forEach((nav) => {
        if (nav.subItems) {
          nav.subItems.forEach((subItem) => {
            if (isActive(subItem.path)) {
              setOpenSubmenu({ groupLabel: group.label, itemKey: itemOrderKey(nav) });
              submenuMatched = true;
            }
          });
        }
      });
    });

    if (!submenuMatched) {
      setOpenSubmenu(null);
    }
  }, [location, isActive, navGroups, isEditingOrder]);

  useEffect(() => {
    if (openSubmenu !== null) {
      const key = `${openSubmenu.groupLabel}${ORDER_KEY_SEPARATOR}${openSubmenu.itemKey}`;
      if (subMenuRefs.current[key]) {
        setSubMenuHeight((prevHeights) => ({
          ...prevHeights,
          [key]: subMenuRefs.current[key]?.scrollHeight || 0,
        }));
      }
    }
    // navGroups 도 본다 — 편집 중 묶음에 메뉴를 넣고 빼면 펼친 높이가 달라져야 잘리지 않는다.
  }, [openSubmenu, navGroups]);

  const handleSubmenuToggle = (groupLabel: string, itemKey: string) => {
    setOpenSubmenu((prevOpenSubmenu) => {
      if (
        prevOpenSubmenu &&
        prevOpenSubmenu.groupLabel === groupLabel &&
        prevOpenSubmenu.itemKey === itemKey
      ) {
        return null;
      }
      return { groupLabel, itemKey };
    });
  };

  const renderMenuItems = (groupLabel: string, items: NavItem[]) => {
    // 이 그룹 안에 있는 하위묶음 — "어디에 넣을까" 목록의 후보다.
    const folders = items.filter((item) => item.folderKey);
    return (
    <ul className="flex flex-col gap-1">
      {items
        .filter((nav) => {
          if (isEditingOrder) return true;
          if (nav.hidden) return false;
          // 안이 비었거나 전부 숨겨진 하위묶음은 눌러도 나올 게 없어 평소 화면에서 뺀다.
          if (nav.folderKey) return (nav.subItems ?? []).some((subItem) => !subItem.hidden);
          return true;
        })
        .map((nav) => {
        const itemKey = itemOrderKey(nav);
        const folderKey = nav.folderKey;
        return (
        <li key={itemKey}>
          <SortableRow
            id={sortableId("item", groupLabel, itemKey)}
            editing={isEditingOrder}
            className={`flex items-center ${nav.hidden ? "opacity-40" : ""}`}
          >
            {(handle) => (
              <>
                {handle}
                {nav.subItems ? (
                  <button
                    onClick={() =>
                      handleSubmenuToggle(groupLabel, itemOrderKey(nav))
                    }
                    className={`menu-item group flex-1 ${
                      openSubmenu?.groupLabel === groupLabel &&
                      openSubmenu?.itemKey === itemOrderKey(nav)
                        ? "menu-item-active"
                        : "menu-item-inactive"
                    } cursor-pointer ${
                      !isExpanded && !isHovered
                        ? "xl:justify-center"
                        : "xl:justify-start"
                    }`}
                  >
                    <span
                      className={`menu-item-icon-size  ${
                        openSubmenu?.groupLabel === groupLabel &&
                        openSubmenu?.itemKey === itemOrderKey(nav)
                          ? "menu-item-icon-active"
                          : "menu-item-icon-inactive"
                      }`}
                    >
                      {nav.icon}
                    </span>

                    {(isExpanded || isHovered || isMobileOpen) && (
                      <span className="menu-item-text">{nav.name}</span>
                    )}
                    {nav.new && (isExpanded || isHovered || isMobileOpen) && (
                      <span
                        className={`ml-auto absolute right-10 ${
                          openSubmenu?.groupLabel === groupLabel &&
                          openSubmenu?.itemKey === itemOrderKey(nav)
                            ? "menu-dropdown-badge-active"
                            : "menu-dropdown-badge-inactive"
                        } menu-dropdown-badge`}
                      >
                        new
                      </span>
                    )}
                    {(isExpanded || isHovered || isMobileOpen) && (
                      <ChevronDownIcon
                        className={`ml-auto w-5 h-5 transition-transform duration-200 ${
                          openSubmenu?.groupLabel === groupLabel &&
                          openSubmenu?.itemKey === itemOrderKey(nav)
                            ? "rotate-180 text-brand-500"
                            : ""
                        }`}
                      />
                    )}
                  </button>
                ) : (
                  nav.path && (
                    <Link
                      to={nav.path}
                      onClick={isEditingOrder ? discardOrderDraft : undefined}
                      className={`menu-item group flex-1 ${
                        isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                      }`}
                    >
                      <span
                        className={`menu-item-icon-size ${
                          isActive(nav.path)
                            ? "menu-item-icon-active"
                            : "menu-item-icon-inactive"
                        }`}
                      >
                        {nav.icon}
                      </span>
                      {(isExpanded || isHovered || isMobileOpen) && (
                        <span className="menu-item-text">{nav.name}</span>
                      )}
                      {nav.new && (isExpanded || isHovered || isMobileOpen) && (
                        <span className="menu-dropdown-badge menu-dropdown-badge-inactive ml-auto">
                          new
                        </span>
                      )}
                      {nav.badge != null && nav.badge > 0 && (
                        <span
                          data-testid="counsel-pending-draft-badge"
                          title={`답장 기다리는 상담 ${nav.badge}건`}
                          className="ml-auto inline-flex min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                          aria-label={`답장 기다리는 상담 ${nav.badge}건`}
                        >
                          {nav.badge > 99 ? "99+" : nav.badge}
                        </span>
                      )}
                    </Link>
                  )
                )}
                {isEditingOrder && (isExpanded || isHovered || isMobileOpen) && (
                  <span className="ml-1 flex shrink-0 items-center gap-0.5">
                    {folderKey ? (
                      <>
                        <button
                          type="button"
                          className={EDIT_ACTION_BTN}
                          title="묶음 이름 바꾸기"
                          aria-label="묶음 이름 바꾸기"
                          onClick={() => setRenamingFolder({ key: folderKey, label: nav.name })}
                        >
                          이름
                        </button>
                        <button
                          type="button"
                          className={EDIT_ACTION_BTN}
                          title="묶음 없애기 — 안에 든 메뉴는 밖으로 나옵니다"
                          aria-label="묶음 없애기"
                          onClick={() => dissolveFolder(groupLabel, folderKey)}
                        >
                          해제
                        </button>
                      </>
                    ) : (
                      nav.path &&
                      !nav.subItems &&
                      folders.length > 0 && (
                        <select
                          className="shrink-0 cursor-pointer appearance-none rounded bg-transparent px-1 text-center text-[11px] text-gray-400 hover:text-brand-500"
                          value=""
                          title="하위묶음에 넣기"
                          aria-label="하위묶음에 넣기"
                          onChange={(event) => {
                            if (event.target.value) {
                              moveIntoFolder(groupLabel, itemKey, event.target.value);
                            }
                          }}
                        >
                          <option value="">넣기</option>
                          {folders.map((folder) => (
                            <option key={folder.folderKey} value={folder.folderKey}>
                              {folder.name}
                            </option>
                          ))}
                        </select>
                      )
                    )}
                    <button
                      type="button"
                      className={EDIT_ACTION_BTN}
                      title={nav.hidden ? "다시 보이게 하기" : "이 메뉴 숨기기"}
                      aria-label={nav.hidden ? "다시 보이게 하기" : "이 메뉴 숨기기"}
                      onClick={() => toggleItemHidden(groupLabel, itemKey)}
                    >
                      {nav.hidden ? "보이기" : "숨기기"}
                    </button>
                  </span>
                )}
              </>
            )}
          </SortableRow>
          {isEditingOrder && folderKey && renamingFolder?.key === folderKey && (
            <div className="mb-1 ml-6 flex items-center gap-1">
              <input
                autoFocus
                value={renamingFolder.label}
                onChange={(event) =>
                  setRenamingFolder({ key: folderKey, label: event.target.value })
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") renameFolder(groupLabel, folderKey, renamingFolder.label);
                  if (event.key === "Escape") setRenamingFolder(null);
                }}
                className="min-w-0 flex-1 rounded border border-gray-200 px-1.5 py-1 text-xs dark:border-gray-700 dark:bg-gray-900"
              />
              <button
                type="button"
                className={EDIT_ACTION_BTN}
                onClick={() => renameFolder(groupLabel, folderKey, renamingFolder.label)}
              >
                확인
              </button>
              <button type="button" className={EDIT_ACTION_BTN} onClick={() => setRenamingFolder(null)}>
                취소
              </button>
            </div>
          )}
          {nav.subItems && (isExpanded || isHovered || isMobileOpen) && (
            <div
              ref={(el) => {
                subMenuRefs.current[subItemOrderKey(groupLabel, nav)] = el;
              }}
              className="overflow-hidden transition-all duration-300"
              style={{
                height:
                  openSubmenu?.groupLabel === groupLabel &&
                  openSubmenu?.itemKey === itemOrderKey(nav)
                    ? `${subMenuHeight[subItemOrderKey(groupLabel, nav)]}px`
                    : "0px",
              }}
            >
              <SortableContext
                items={nav.subItems.map((subItem) =>
                  sortableId("sub", subItemOrderKey(groupLabel, nav), subItem.path),
                )}
                strategy={verticalListSortingStrategy}
              >
                <ul className={`mt-2 space-y-1 ${isEditingOrder ? "ml-4" : "ml-9"}`}>
                  {nav.subItems
                    .filter((subItem) => isEditingOrder || !subItem.hidden)
                    .map((subItem) => (
                    <li key={subItem.path}>
                      <SortableRow
                        id={sortableId(
                          "sub",
                          subItemOrderKey(groupLabel, nav),
                          subItem.path,
                        )}
                        editing={isEditingOrder}
                        className={`flex items-center ${subItem.hidden ? "opacity-40" : ""}`}
                      >
                        {(handle) => (
                          <>
                            {handle}
                            <Link
                              to={subItem.path}
                              onClick={isEditingOrder ? discardOrderDraft : undefined}
                              className={`menu-dropdown-item flex-1 ${
                                isActive(subItem.path)
                                  ? "menu-dropdown-item-active"
                                  : "menu-dropdown-item-inactive"
                              }`}
                            >
                              {subItem.name}
                              <span className="flex items-center gap-1 ml-auto">
                                {subItem.new && (
                                  <span
                                    className={`ml-auto ${
                                      isActive(subItem.path)
                                        ? "menu-dropdown-badge-active"
                                        : "menu-dropdown-badge-inactive"
                                    } menu-dropdown-badge`}
                                  >
                                    new
                                  </span>
                                )}
                                {subItem.badge != null && subItem.badge > 0 && (
                                  <span
                                    data-testid="counsel-pending-draft-badge"
                                    title={`답장 기다리는 상담 ${subItem.badge}건`}
                                    className="inline-flex min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[10px] font-semibold leading-none text-white"
                                    aria-label={`답장 기다리는 상담 ${subItem.badge}건`}
                                  >
                                    {subItem.badge > 99 ? "99+" : subItem.badge}
                                  </span>
                                )}
                                {subItem.pro && (
                                  <span
                                    className={`ml-auto ${
                                      isActive(subItem.path)
                                        ? "menu-dropdown-badge-pro-active"
                                        : "menu-dropdown-badge-pro-inactive"
                                    } menu-dropdown-badge-pro`}
                                  >
                                    pro
                                  </span>
                                )}
                              </span>
                            </Link>
                            {isEditingOrder && (
                              <span className="ml-1 flex shrink-0 items-center gap-0.5">
                                {folderKey && (
                                  <button
                                    type="button"
                                    className={EDIT_ACTION_BTN}
                                    title="묶음 밖으로 빼기"
                                    aria-label="묶음 밖으로 빼기"
                                    onClick={() =>
                                      moveOutOfFolder(groupLabel, folderKey, subItem.path)
                                    }
                                  >
                                    빼기
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className={EDIT_ACTION_BTN}
                                  title={subItem.hidden ? "다시 보이게 하기" : "이 메뉴 숨기기"}
                                  aria-label={subItem.hidden ? "다시 보이게 하기" : "이 메뉴 숨기기"}
                                  onClick={() => toggleSubHidden(groupLabel, itemKey, subItem.path)}
                                >
                                  {subItem.hidden ? "보이기" : "숨기기"}
                                </button>
                              </span>
                            )}
                          </>
                        )}
                      </SortableRow>
                    </li>
                  ))}
                </ul>
              </SortableContext>
            </div>
          )}
        </li>
        );
      })}
    </ul>
    );
  };

  return (
    <>
    {/* 평소 z 는 반드시 모달 층(z-99999 이상) **아래**여야 한다 — 예전엔 z-[100000] 이라 사이드바가
        모달 위에 그려졌고, 화면 왼쪽 290px 에 걸친 모달 내용(검색창·목록 첫 칸)이 메뉴에 가려져
        클릭이 메뉴로 새 나갔다(티켓 #145 → 그 화면만 left 로 비켜 갔고, 티켓 #156 서식 출력 창의
        "검색창을 눌러도 글자가 안 써짐"이 같은 원인이었다). 상대 순서는 그대로 두고 한 칸씩
        내렸다: 콘텐츠 < 상단바(99997) < 사이드바(99998) < 모달(99999~).
        단, 접힌 메뉴를 마우스로 펼쳐 보는 동안에는 모든 중첩 모달·확인창보다 위로 잠깐 올린다.
        이때만 메뉴 이름과 링크가 딤에 가리지 않고 눌리며, 마우스를 떼면 평소 층으로 돌아온다. */}
    <aside
      className={`fixed  flex flex-col  top-0 px-5 left-0 bg-white dark:bg-gray-900 dark:border-gray-800 text-gray-900 h-screen transition-all duration-300 ease-in-out border-r border-gray-200
        ${isHovered && !isExpanded ? "z-[99998] xl:z-[1000000]" : "z-[99998]"}
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isHovered && !isExpanded ? "shadow-xl dark:shadow-black/40" : ""}
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"} xl:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      // ⚙️ 설정을 열어 둔 동안에는 마우스가 나가도 접지 않는다 — 접히면 패널만 허공에 남고
      //    뒤의 메뉴 글자가 좁은 띠에 눌려 세로로 깨진다(2026-08-28 실측).
      onMouseLeave={() => {
        if (!isSettingsOpen) setIsHovered(false);
      }}
    >
      {/* 메뉴 폭과 무관한 상단 스트립: 오른쪽 상단 바 첫 줄(76px)과 경계를 맞춘다.
          ⚠️ 접힌 상태(90px)에서는 폭을 함께 줄여야 한다 — 예전엔 250px 로 고정이라 로고 줄이
             좁은 띠 밖으로 삐져나와 본문을 덮었고, 그걸 피하려고 상단 바가 왼쪽을 206px 이나
             비워 두고 있었다(=본문이 그만큼 좁았다). 넘치는 건 잘라내고, 이름·바로가기·배율은
             펼쳐졌을 때만 그린다. */}
      <div
        className={`flex h-[76px] shrink-0 flex-col overflow-hidden ${
          isExpanded || isHovered || isMobileOpen ? "w-[250px]" : "w-[50px]"
        }`}
      >
        <div className="flex h-[38px] items-center justify-start">
          <Link
            to="/"
            aria-label="처음화면으로 이동"
            onClick={isEditingOrder ? discardOrderDraft : undefined}
          >
            <span className="whitespace-nowrap text-xl font-bold text-gray-800 dark:text-white/90">
              {/* 접힌 띠에는 두 글자 약칭만 — 전체 이름은 폭이 모자라 잘린다. */}
              {isExpanded || isHovered || isMobileOpen ? APP_NAME : "PB"}
            </span>
          </Link>
          {(isExpanded || isHovered || isMobileOpen) && (
            <Link
              to="/"
              onClick={isEditingOrder ? discardOrderDraft : undefined}
              className="ml-3 shrink-0 rounded-md border border-gray-200 px-2.5 py-1.5 text-xs font-medium text-gray-600 transition-colors hover:border-brand-300 hover:bg-brand-50 hover:text-brand-600 dark:border-gray-700 dark:text-gray-300 dark:hover:border-brand-500/50 dark:hover:bg-brand-500/10 dark:hover:text-brand-300"
            >
              처음화면 이동하기
            </Link>
          )}
        </div>
        {/* 로고 아래 줄 — 2026-08-28 부터 **⚙️ 한 칸**이다.
            화면 크기 조절(티켓 #57)·메뉴 순서 편집·메뉴 펼침 고정(옛 상단 띠 햄버거)이 전부
            그 안으로 들어갔다. 가끔 만지는 설정이 늘 자리를 차지하던 걸 접은 것이다.
            ⚠️ 편집 중에는 [완료]·[되돌리기]가 여기 그대로 남는다 — 빠져나올 길을 메뉴 안에
               묻으면 안 된다. */}
        <div className="flex h-[38px] items-center gap-2">
          <button
            type="button"
            onClick={() => {
              // 접힌 띠에서 열어도 메뉴가 펼쳐진 채 있어야 패널이 제자리에 선다.
              setIsHovered(true);
              setSettingsOpen((prev) => !prev);
            }}
            aria-expanded={isSettingsOpen}
            aria-haspopup="dialog"
            aria-label="메뉴 설정"
            title="화면 크기·메뉴 순서·메뉴 고정"
            className={`flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg border transition ${
              isSettingsOpen
                ? "border-brand-500 text-brand-600 dark:text-brand-300"
                : "border-gray-200 text-gray-500 hover:bg-gray-100 dark:border-gray-800 dark:text-gray-400 dark:hover:bg-gray-800"
            }`}
          >
            <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden>
              <circle cx="10" cy="10" r="2.6" stroke="currentColor" strokeWidth="1.5" />
              <path
                d="M10 2.5l1 2.1 2.3-.4.6 2.2 2.1 1-1.2 2 1.2 2-2.1 1-.6 2.2-2.3-.4-1 2.1-1-2.1-2.3.4-.6-2.2-2.1-1 1.2-2-1.2-2 2.1-1 .6-2.2 2.3.4 1-2.1z"
                stroke="currentColor"
                strokeWidth="1.3"
                strokeLinejoin="round"
              />
            </svg>
          </button>

          {isEditingOrder && (isExpanded || isHovered || isMobileOpen) && (
            <div className="hidden min-w-0 items-center gap-1 xl:flex">
              <button
                type="button"
                onClick={finishOrderEditing}
                disabled={isSavingOrder}
                aria-label="메뉴 편집 완료"
                className="shrink-0 whitespace-nowrap rounded-md bg-brand-500 px-2 py-1.5 text-xs font-semibold text-white hover:bg-brand-600 disabled:opacity-50"
              >
                {isSavingOrder ? "저장 중…" : "완료"}
              </button>
              <button
                type="button"
                onClick={restoreDefaultOrder}
                disabled={isSavingOrder}
                title="숨김·묶음·순서를 모두 지우고 기본 메뉴로 되돌립니다"
                className="shrink-0 whitespace-nowrap rounded-md border border-gray-200 px-2 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                되돌리기
              </button>
            </div>
          )}
        </div>
      </div>

      {isSettingsOpen && (
        <SidebarSettingsPanel
          pinned={isExpanded}
          onTogglePin={toggleSidebar}
          canEditOrder={isStoreManagerUser && orderLoaded && !isEditingOrder}
          onStartOrderEditing={startOrderEditing}
          onClose={() => setSettingsOpen(false)}
        />
      )}
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        {isEditingOrder && (isExpanded || isHovered) && (
          <p className="mb-3 hidden text-[11px] leading-4 text-gray-400 xl:block">
            메뉴 오른쪽 [숨기기] 로 감추고, [＋묶음] 으로 만든 묶음에 [넣기]·[빼기] 로 옮깁니다.
            <br />
            숨김은 화면 정리일 뿐 권한이 아닙니다 — 주소를 직접 입력하면 열립니다.
          </p>
        )}
        {orderError && isEditingOrder && (
          <p role="alert" className="mb-3 text-xs text-error-600 dark:text-error-400">
            저장하지 못했습니다. 잠시 후 다시 눌러 주세요.
          </p>
        )}
        <nav className="mb-6">
          <DndContext
            sensors={sensors}
            collisionDetection={sidebarCollisionDetection}
            onDragEnd={handleOrderDragEnd}
          >
            <SortableContext
              items={visibleNavGroups.map((group) => sortableId("group", "root", group.label))}
              strategy={verticalListSortingStrategy}
            >
              <div className="flex flex-col gap-4">
                {visibleNavGroups.map((group, groupIndex) => (
                  <SortableRow
                    key={group.label}
                    id={sortableId("group", "root", group.label)}
                    editing={isEditingOrder}
                    className={group.hidden ? "opacity-40" : ""}
                  >
                    {(groupHandle) => (
                      <>
                        {isExpanded || isHovered || isMobileOpen ? (
                          <div className="mb-2 flex items-center">
                            {groupHandle}
                            <h2 className="text-xs uppercase leading-[20px] text-gray-400">
                              {group.label}
                            </h2>
                            {isEditingOrder && (
                              <span className="ml-auto flex shrink-0 items-center gap-0.5">
                                <button
                                  type="button"
                                  className={EDIT_ACTION_BTN}
                                  title="이 그룹 안에 하위묶음 만들기"
                                  aria-label="하위묶음 만들기"
                                  onClick={() => {
                                    setNewFolderFor(group.label);
                                    setNewFolderLabel("");
                                  }}
                                >
                                  ＋묶음
                                </button>
                                <button
                                  type="button"
                                  className={EDIT_ACTION_BTN}
                                  title={group.hidden ? "다시 보이게 하기" : "이 그룹 통째로 숨기기"}
                                  aria-label={group.hidden ? "다시 보이게 하기" : "이 그룹 숨기기"}
                                  onClick={() => toggleGroupHidden(group.label)}
                                >
                                  {group.hidden ? "보이기" : "숨기기"}
                                </button>
                              </span>
                            )}
                          </div>
                        ) : groupIndex > 0 ? (
                          <div className="my-2 border-t border-gray-100 dark:border-gray-800" />
                        ) : (
                          <div className="mb-2 flex justify-center">
                            <HorizontaLDots className="size-6 text-gray-400" />
                          </div>
                        )}
                        {isEditingOrder && newFolderFor === group.label && (
                          <div className="mb-2 flex items-center gap-1">
                            <input
                              autoFocus
                              value={newFolderLabel}
                              placeholder="묶음 이름"
                              onChange={(event) => setNewFolderLabel(event.target.value)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter") createFolder(group.label, newFolderLabel);
                                if (event.key === "Escape") setNewFolderFor(null);
                              }}
                              className="min-w-0 flex-1 rounded border border-gray-200 px-1.5 py-1 text-xs dark:border-gray-700 dark:bg-gray-900"
                            />
                            <button
                              type="button"
                              className={EDIT_ACTION_BTN}
                              onClick={() => createFolder(group.label, newFolderLabel)}
                            >
                              만들기
                            </button>
                            <button
                              type="button"
                              className={EDIT_ACTION_BTN}
                              onClick={() => setNewFolderFor(null)}
                            >
                              취소
                            </button>
                          </div>
                        )}
                        <SortableContext
                          items={group.items.map((item) =>
                            sortableId("item", group.label, itemOrderKey(item)),
                          )}
                          strategy={verticalListSortingStrategy}
                        >
                          {renderMenuItems(group.label, group.items)}
                        </SortableContext>
                      </>
                    )}
                  </SortableRow>
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </nav>
      </div>
    </aside>
    </>
  );
};

export default AppSidebar;
